import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Home, Crown, CreditCard, Calendar, Mail, User as UserIcon, Loader2 } from 'lucide-react';
import PremiumBadge from '../components/PremiumBadge';

import { API_URL } from '../config';
const API = API_URL;

const ProfilePage = ({ user, onLogout }) => {
  const [profileData, setProfileData] = useState(null);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);

  useEffect(() => {
    document.title = 'EbonyThroatQueens | Profile';
    fetchProfile();
    fetchSubscriptionStatus();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/user/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfileData(response.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchSubscriptionStatus = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/subscription/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Failed to load subscription status:', error);
    }
  };
  
  const handleSubscribe = async () => {
    setSubscriptionLoading(true);
    try {
      const res = await axios.post(`${API}/subscription/create`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (!res.data?.approval_url) {
        console.error('PayPal response:', res.data);
        throw new Error(res.data?.detail || 'Subscription creation failed');
      }
      
      window.location.href = res.data.approval_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to create subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };
  
  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel your subscription?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/subscription/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Subscription cancelled');
      fetchSubscriptionStatus();
      fetchProfile();
    } catch (error) {
      toast.error('Failed to cancel subscription');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  const calculateDaysRemaining = (endDate) => {
    if (!endDate) return 0;
    const end = new Date(endDate);
    const now = new Date();
    const diff = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, diff);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <p className="text-gray-400">Loading...</p>
      </div>
    );
  }

  const isPremiumActive = subscriptionData?.is_premium && subscriptionData?.subscription_status === 'active';
  const daysRemaining = isPremiumActive ? calculateDaysRemaining(subscriptionData?.subscription_end_date) : 0;

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Header */}
      <header
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{ background: 'rgba(0, 0, 0, 0.8)', borderBottom: '1px solid #2A2A2A' }}
      >
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              onClick={() => window.location.href = '/welcome'}
              variant="ghost"
              size="sm"
              className="text-[#EE1C4E] hover:text-white hover:bg-[#1A1A1A] p-2"
              data-testid="home-button-profile"
              title="Go to Welcome Page"
            >
              <Home className="w-5 h-5" />
            </Button>
            <h1 className="text-2xl font-black inline-flex items-center gap-2" style={{ color: '#EE1C4E' }}>
              <span>My Profile</span>
              {user?.role === 'premium_user' && <PremiumBadge size={18} />}
            </h1>
          </div>

          <Button
            onClick={onLogout}
            variant="outline"
            className="border-gray-700 text-gray-400 hover:text-white"
            data-testid="logout-button"
          >
            Logout
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* User Info Card */}
        <div
          className="p-6 rounded-xl"
          style={{ background: '#121212', border: '1px solid #2A2A2A' }}
          data-testid="user-info-card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-white">Account Information</h2>
            <div className="flex items-center gap-3">
              {profileData?.user?.is_premium && (
                <span
                  className="px-2 py-1 rounded-full text-lg flex items-center"
                  style={{ background: '#EE1C4E' }}
                  data-testid="profile-vip-badge"
                  title="Premium Member"
                >
                  👑
                </span>
              )}
              <Button
                onClick={() => window.location.href = '/profile/edit'}
                className="bg-[#EE1C4E] hover:bg-[#EE1C4E]/90 text-white text-sm"
                data-testid="edit-profile-button"
              >
                Edit Profile
              </Button>
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <UserIcon className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Name</p>
                <p className="text-white font-semibold">{profileData?.user?.name}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Email</p>
                <p className="text-white font-semibold">{profileData?.user?.email}</p>
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-500" />
              <div>
                <p className="text-xs text-gray-500">Member Since</p>
                <p className="text-white font-semibold">{formatDate(profileData?.user?.created_at)}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Dashboard Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Subscription Card */}
          <div
            className="p-6 rounded-xl"
            style={{ background: '#121212', border: '1px solid #2A2A2A' }}
            data-testid="subscription-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <Crown className="w-6 h-6 text-[#EE1C4E]" />
              <h2 className="text-xl font-bold text-white">Premium Subscription</h2>
            </div>

            {isPremiumActive ? (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Status</span>
                  <span className="px-3 py-1 rounded-full text-xs font-bold" style={{ background: '#00C853', color: 'white' }}>
                    Active
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Next Billing</span>
                  <span className="text-white font-semibold text-sm">{formatDate(subscriptionData?.next_billing_date)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-gray-400 text-sm">Days Remaining</span>
                  <span className="text-white font-semibold text-sm">{daysRemaining} days</span>
                </div>
                <Button
                  onClick={handleCancelSubscription}
                  variant="outline"
                  className="w-full mt-2 bg-transparent border-red-500 text-red-500 hover:bg-red-500 hover:text-white"
                >
                  Cancel Subscription
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-gray-400 text-sm mb-3">
                  {subscriptionData?.subscription_status === 'cancelled' 
                    ? 'Subscription Cancelled' 
                    : subscriptionData?.subscription_status === 'expired'
                    ? 'Subscription Expired'
                    : 'No active subscription'}
                </p>
                <div className="space-y-2 mb-4">
                  <p className="text-sm text-gray-300">✅ Ad-free experience</p>
                  <p className="text-sm text-gray-300">✅ Access all premium content</p>
                  <p className="text-sm text-gray-300">✅ Exclusive features</p>
                </div>
                <Button
                  onClick={handleSubscribe}
                  disabled={subscriptionLoading}
                  className="w-full bg-[#EE1C4E] hover:bg-[#D91744] text-white"
                >
                  {subscriptionLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    'Subscribe Now - $9.99/month'
                  )}
                </Button>
              </div>
            )}
          </div>

          {/* Contributor Portal Card */}
          <button
            onClick={() => window.location.href = '/contributor-portal'}
            className="p-6 rounded-xl text-left transition-all hover:brightness-110 cursor-pointer"
            style={{ background: '#121212', border: '1px solid #2A2A2A' }}
            data-testid="contributor-portal-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-6 h-6 flex items-center justify-center">
                <span className="text-xl">✍️</span>
              </div>
              <h2 className="text-xl font-bold text-white">Contributor Portal</h2>
            </div>
            <p className="text-gray-400 text-sm mb-3">
              Create and manage your content
            </p>
            <div className="text-[#EE1C4E] text-sm font-semibold">Access Portal →</div>
          </button>

          {/* Saved Posts Card */}
          <button
            onClick={() => window.location.href = '/saved-posts'}
            className="p-6 rounded-xl text-left transition-all hover:brightness-110 cursor-pointer"
            style={{ background: '#121212', border: '1px solid #2A2A2A' }}
            data-testid="saved-posts-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="w-6 h-6 flex items-center justify-center">
                <span className="text-xl">🔖</span>
              </div>
              <h2 className="text-xl font-bold text-white">Saved Posts</h2>
            </div>
            <p className="text-gray-400 text-sm mb-3">
              Access your bookmarked posts anytime
            </p>
            <div className="text-[#EE1C4E] text-sm font-semibold">View All →</div>
          </button>

          {/* Payment History Card */}
          <button
            onClick={() => {
              // Expand to show payment history or navigate to detailed view
              toast.info('Payment history details');
            }}
            className="p-6 rounded-xl text-left transition-all hover:brightness-110 cursor-pointer"
            style={{ background: '#121212', border: '1px solid #2A2A2A' }}
            data-testid="payment-history-card"
          >
            <div className="flex items-center gap-3 mb-4">
              <CreditCard className="w-6 h-6 text-[#EE1C4E]" />
              <h2 className="text-xl font-bold text-white">Payment History</h2>
            </div>

            {profileData?.payment_history?.length > 0 ? (
              <div>
                <p className="text-gray-400 text-sm mb-2">
                  Latest: ${profileData.payment_history[0].amount} USD
                </p>
                <p className="text-gray-500 text-xs mb-3">
                  {formatDate(profileData.payment_history[0].payment_date)}
                </p>
                <div className="text-[#EE1C4E] text-sm font-semibold">View All →</div>
              </div>
            ) : (
              <div>
                <p className="text-gray-400 text-sm mb-3">No payment history</p>
                <div className="text-gray-500 text-sm">—</div>
              </div>
            )}
          </button>
        </div>
      </main>
    </div>
  );
};

export default ProfilePage;